import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Inner from '../components/Inner';
import PageTitle from '../components/PageTitle';
import { styled as muiStyled } from '@mui/system';
import { TextField } from '@mui/material';
import Button from '@mui/material/Button/Button';
import React, { useState, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../hooks/useRedux';
import { login } from '../store/authSlice';
import SignAlert from '../components/SignAlert';

const Contents = styled.div`
	width: fit-content;
	margin: 0 auto;
`;

const Inputs = styled.form`
	width: 22rem;
	margin: 0 auto;

	@media (max-width: 767px) {
		width: fit-content;
	}
`;

const CustomButton = muiStyled(Button)({
	fontSize: '1.4rem',
	fontWeight: '700',
	marginTop: 35,
});

const SignUpLinkWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: end;
	margin-top: 1.4rem;
`;

function SignIn() {
	const idInputRef = useRef<HTMLInputElement>(null);
	const pwInputRef = useRef<HTMLInputElement>(null);

	const users = useAppSelector((state) => {
		return state.users.users;
	});
	const dispatch = useAppDispatch();

	const navigate = useNavigate();

	const [inputs, setInputs] = useState({
		id: '',
		pw: '',
	});

	const [errors, setErrors] = useState({
		isIdInputEmpty: false,
		isPwInputEmpty: false,
		isIdNotFound: false,
		isPwIncorrect: false,
	});

	// 구조분해할당
	const { id, pw } = inputs;
	// same with
	// const id = inputs.id;
	// const pw = inputs.pw;

	const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value, name } = e.target;

		setInputs({
			...inputs, // 기존의 input 객체를 복사한 뒤
			[name]: value, // name 키를 가진 값을 value 로 설정
		});
	};

	const handleSubmit = (e: React.SyntheticEvent) => {
		e.preventDefault();

		const initialErrors = {
			isIdInputEmpty: false,
			isPwInputEmpty: false,
			isIdNotFound: false,
			isPwIncorrect: false,
		};

		// 아이디를 입력하지 않은 경우
		if (id === '') {
			idInputRef.current?.focus();
			setErrors({ ...initialErrors, isIdInputEmpty: true });
		}
		// 비밀번호를 입력하지 않은 경우
		else if (pw === '') {
			pwInputRef.current?.focus();
			setErrors({ ...initialErrors, isPwInputEmpty: true });
		} else {
			e.preventDefault();

			// 입력한 아이디가 존재하는 아이디인지 먼저 검사
			const checkInputId = users.find((user) => user.id === id);

			// 검사해서 아이디가 존재하는 경우
			if (checkInputId) {
				// 로그인 성공
				if (checkInputId.pw === pw) {
					const loggedInUser = { ...checkInputId, cart: checkInputId.cart || [] };
					setInputs({
						id: '',
						pw: '',
					});
					setErrors(initialErrors);
					dispatch(login(loggedInUser));
					return navigate('/');
				}
				// 비밀번호 틀렸을 경우
				else {
					setInputs({
						...inputs,
						pw: '',
					});
					pwInputRef.current?.focus();
					setErrors({ ...initialErrors, isPwIncorrect: true });
				}
			}
			// 아이디가 존재하지 않을 경우
			else {
				setInputs({
					id: '',
					pw: '',
				});
				idInputRef.current?.focus();
				setErrors({ ...initialErrors, isIdNotFound: true });
			}
		}
	};

	const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			handleSubmit(e);
		}
	};

	return (
		<Inner>
			<PageTitle korean>로그인</PageTitle>
			<Contents>
				<Inputs>
					<TextField
						name='id'
						inputRef={idInputRef}
						onChange={handleInput}
						onKeyDown={handleEnter}
						value={id}
						margin='normal'
						label='아이디'
						fullWidth
						autoFocus
					/>
					<TextField
						name='pw'
						inputRef={pwInputRef}
						onChange={handleInput}
						onKeyDown={handleEnter}
						value={pw}
						margin='normal'
						label='비밀번호'
						type='password'
						fullWidth
					/>
					<CustomButton
						onClick={handleSubmit}
						type='submit'
						fullWidth
						variant='contained'
					>
						로그인하기
					</CustomButton>
				</Inputs>
				<SignUpLinkWrapper>
					<Link to='/signup'>계정이 없으신가요?</Link>
				</SignUpLinkWrapper>
				{errors.isIdInputEmpty && (
					<SignAlert severity='error'>아이디를 입력해주세요.</SignAlert>
				)}
				{errors.isPwInputEmpty && (
					<SignAlert severity='error'>비밀번호를 입력해주세요.</SignAlert>
				)}
				{errors.isIdNotFound && (
					<SignAlert severity='error'>존재하지 않는 아이디입니다.</SignAlert>
				)}
				{errors.isPwIncorrect && (
					<SignAlert severity='error'>비밀번호가 틀렸습니다.</SignAlert>
				)}
			</Contents>
		</Inner>
	);
}

export default SignIn;
